import React, { useCallback, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import CircularProgress from '../../common/CircularProgress';
import { Button } from '@/components/ui/button';
import apiCoreAdapter from '../../../hooks/apiCoreAdapter';
import { TherapeuticStyleQuestion, TherapeuticStyleState } from '../../../hooks/api/therapeuticStyle';

interface TherapeuticStyleTabProps {
  state: TherapeuticStyleState | null;
  loading: boolean;
  /** El padre mergea el mapa completo de respuestas guardadas en su copia de `state`
   *  (misma fuente que decide si la tab sigue visible — ver PsychologistProfile). */
  onAnswersSaved: (answers: Record<string, string>) => void;
}

/** Busca, entre las opciones de una pregunta, cuál corresponde al valor ya guardado
 *  (`state.answers[realFilterKey]`). Dos o más opciones pueden compartir `category` (y por
 *  lo tanto el mismo valor resuelto — ver el comentario grande más abajo), así que esto es
 *  best-effort: se queda con la primera que matchea. No hay forma de saber con certeza cuál
 *  de las dos se eligió en su momento, porque el backend solo guarda el valor resuelto, no
 *  el id de la opción. */
function findOptionIdForSavedValue(
  question: TherapeuticStyleQuestion,
  savedValue: string | undefined,
): string | undefined {
  if (!savedValue) return undefined;
  return question.options.find((o) => question.categoryFilterValues[o.category] === savedValue)?.id;
}

const TherapeuticStyleTab: React.FC<TherapeuticStyleTabProps> = ({
  state,
  loading,
  onAnswersSaved,
}) => {
  // Borrador local, indexado por `realFilterKey` -> `option.id` de la opción elegida (NO por
  // el valor resuelto). Seleccionar una opción solo actualiza esto, nunca guarda — el
  // guardado real (todas las respuestas juntas, un solo request) pasa por "Guardar respuestas".
  //
  // Por qué por option.id y no por valor: dos opciones de una misma pregunta pueden compartir
  // `category` (ej. la B y la D mapean las dos a "Fluida" en una de las preguntas reales) —
  // "category" acá es un agrupador semántico para el filtro real, no un id de opción. Comparar
  // por el VALOR resuelto (`categoryFilterValues[category]`) hacía que elegir la B pintara
  // también la D, porque las dos resuelven al mismo string. Comparando por `option.id` (único
  // por opción) cada botón solo se pinta si es exactamente el que se clickeó.
  const [draftSelections, setDraftSelections] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state) return;
    const next: Record<string, string> = {};
    for (const question of state.questions) {
      const optionId = findOptionIdForSavedValue(question, state.answers[question.realFilterKey]);
      if (optionId) next[question.realFilterKey] = optionId;
    }
    setDraftSelections(next);
  }, [state]);

  const resolveValue = useCallback(
    (question: TherapeuticStyleQuestion, optionId: string | undefined): string | undefined => {
      const option = question.options.find((o) => o.id === optionId);
      return option ? question.categoryFilterValues[option.category] : undefined;
    },
    [],
  );

  const selectOption = useCallback(
    (question: TherapeuticStyleQuestion, optionId: string) => {
      if (saving) return;
      setDraftSelections((prev) => ({ ...prev, [question.realFilterKey]: optionId }));
    },
    [saving],
  );

  const isDirty =
    !!state &&
    state.questions.some(
      (q) => resolveValue(q, draftSelections[q.realFilterKey]) !== state.answers[q.realFilterKey],
    );

  const handleSave = useCallback(async () => {
    if (!state || saving) return;
    setSaving(true);
    try {
      const preferences: Record<string, string[]> = {};
      const nextAnswers: Record<string, string> = {};
      for (const question of state.questions) {
        const value = resolveValue(question, draftSelections[question.realFilterKey]);
        if (value) {
          preferences[question.realFilterKey] = [value];
          nextAnswers[question.realFilterKey] = value;
        }
      }
      await apiCoreAdapter.updatePsychologistProfile({ preferences });
      onAnswersSaved(nextAnswers);
    } finally {
      setSaving(false);
    }
  }, [state, saving, draftSelections, resolveValue, onAnswersSaved]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <CircularProgress label="Cargando…" />
      </div>
    );
  }

  // El padre es quien decide si esta tab está disponible (mismo `state` que gatea el
  // botón de la tab) — acá solo evitamos renderizar vacío en el frame entre que
  // `state.questions` queda en `[]` y el padre nos saca de esta tab.
  if (!state || state.questions.length === 0) return null;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-base font-semibold text-[#4a3d73] m-0">Tu estilo terapéutico</h3>
        <p className="text-sm text-gray-500 mt-1.5 mb-0 leading-relaxed">
          Estas respuestas ayudan a que te asignemos pacientes con los que tengas más
          afinidad de estilo de trabajo. Podés cambiarlas cuando quieras.
        </p>
      </div>

      {state.questions.map((question, index) => (
        <TherapeuticStyleQuestionCard
          key={question.id}
          question={question}
          questionNumber={index + 1}
          totalQuestions={state.questions.length}
          selectedOptionId={draftSelections[question.realFilterKey] ?? null}
          disabled={saving}
          onSelect={(optionId) => selectOption(question, optionId)}
        />
      ))}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? 'Guardando…' : 'Guardar respuestas'}
        </Button>
      </div>
    </div>
  );
};

const TherapeuticStyleQuestionCard: React.FC<{
  question: TherapeuticStyleQuestion;
  questionNumber: number;
  totalQuestions: number;
  selectedOptionId: string | null;
  disabled: boolean;
  onSelect: (optionId: string) => void;
}> = ({ question, questionNumber, totalQuestions, selectedOptionId, disabled, onSelect }) => (
  <div className="border border-gray-200 rounded-2xl p-7 bg-white">
    <span className="inline-block text-[0.6875rem] font-semibold text-[#6D57BC] bg-[#f5f3ff] rounded-full px-3 py-1 mb-4">
      Pregunta {questionNumber} de {totalQuestions}
    </span>

    <p className="text-sm text-gray-600 italic border-l-2 border-[#e4defa] pl-4 m-0 mb-5 leading-relaxed">
      {question.escenario}
    </p>
    <p className="text-base font-semibold text-gray-900 m-0 mb-8">{question.pregunta}</p>

    <div className="grid gap-3">
      {question.options.map((option) => {
        const isSelected = option.id === selectedOptionId;
        return (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(option.id)}
            className={`text-left flex items-start gap-3 px-5 py-4 rounded-xl border text-sm transition-colors ${
              isSelected
                ? 'border-[#6D57BC] bg-[#f5f3ff] text-[#4a3d73]'
                : 'border-gray-200 text-gray-700 hover:border-[#6D57BC] hover:bg-[#f5f3ff]/40'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`flex-shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-xs font-semibold mt-0.5 ${
                isSelected ? 'border-[#6D57BC] bg-[#6D57BC] text-white' : 'border-gray-300 text-gray-500'
              }`}
            >
              {isSelected ? <Check size={13} /> : option.letter}
            </span>
            <span className="leading-relaxed">{option.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

export default TherapeuticStyleTab;
